import React from 'react';
import { X, Filter } from 'lucide-react';
import { useRiskContext } from '../context/RiskContext.jsx';

export default function FiltersPane({ onManageCategories, onManageStatus, onManageActions, onHideSidebar }) {
  const {
    filters,
    setFilters,
    clearFilters,
    categories,
    riskActions,
    statusOptions,
    getUniqueOwners,
    filteredRisks,
    risks,
    loadSampleData,
    clearAllData
  } = useRiskContext();

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <div className="p-4 bg-gray-50 space-y-6">
      {/* Header with Hide Sidebar Button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-600" />
          <h3 className="font-semibold text-gray-800">Filters</h3>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <X size={12} />
              Clear
            </button>
          )}
          {onHideSidebar && (
            <button
              onClick={onHideSidebar}
              className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1 px-2 py-1 rounded border border-gray-300 hover:bg-gray-100"
              title="Hide Sidebar"
            >
              <X size={12} />
              Hide
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {/* Results Count */}
        <div className="bg-white p-3 rounded-lg border">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-800">{filteredRisks.length}</span> of{' '}
            <span className="font-semibold text-gray-800">{risks.length}</span> risks
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Status</label>
            {onManageStatus && (
              <button onClick={onManageStatus} className="text-xs text-blue-600 hover:underline">Manage</button>
            )}
          </div>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Category</label>
            {onManageCategories && (
              <button onClick={onManageCategories} className="text-xs text-blue-600 hover:underline">Manage</button>
            )}
          </div>
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        {/* Action Filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Action</label>
            {onManageActions && (
              <button onClick={onManageActions} className="text-xs text-blue-600 hover:underline">Manage</button>
            )}
          </div>
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Actions</option>
            {riskActions.map(action => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>
        </div>

        {/* Owner Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Owner</label>
          <select
            value={filters.owner}
            onChange={(e) => handleFilterChange('owner', e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Owners</option>
            {getUniqueOwners().map(owner => (
              <option key={owner} value={owner}>{owner}</option>
            ))}
          </select>
        </div>

        {/* Deadline Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
          <select
            value={filters.deadline}
            onChange={(e) => handleFilterChange('deadline', e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Deadlines</option>
            <option value="overdue">Overdue</option>
            <option value="thisWeek">This Week</option>
            <option value="noDeadline">No Deadline</option>
          </select>
        </div>
      </div>

      {/* Load Sample Records Button */}
      <div className="pt-4 border-t border-gray-200 space-y-2">
        <button
          onClick={loadSampleData}
          className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 active:bg-blue-800 font-medium shadow"
        >
          Load Sample Records ({risks.length})
        </button>
        
        {/* Clear All Data Button */}
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
              clearAllData();
            }
          }}
          className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 active:bg-red-800 font-medium shadow"
          disabled={risks.length === 0}
        >
          Clear All Data
        </button>
      </div>
    </div>
  );
} 